import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { findPractica } from "@/features/projects/data/catalog";
import { recordExamAttempt } from "@/features/progress/progressApi";
import { getGradeFunction } from "@/features/projects/grading/gradeFunctions";
import type { TaskResult } from "@/features/projects/types";

const EXAM_DURATION = 50 * 60;

type Mode = "practica" | "examen";

interface PendingExam {
  practicaId: string;
  startedAt: Date;
  results: Record<string, Record<string, boolean>>;
  clientId: string;
}

interface ExamContextType {
  mode: Mode;
  setMode: (mode: Mode) => void;
  examActive: boolean;
  examFinished: boolean;
  timeRemaining: number;
  practicaId: string | null;
  currentProjectId: string | null;
  examResults: Record<string, TaskResult[]> | null;
  examProjectSnapshots: Record<string, TaskResult[]>;
  examTaskSelections: Record<string, number[]>;
  examStartedProjects: string[];
  gradingAll: boolean;
  /** The last submitted exam could not be saved in the cloud yet. */
  examSyncFailed: boolean;
  retryingExamSave: boolean;
  retryExamSave: () => Promise<void>;
  startExam: (practicaId: string) => void;
  submitExam: () => Promise<void>;
  resetExam: () => void;
  saveExamProjectSnapshot: (projectId: string, results: TaskResult[]) => void;
  toggleExamTaskSelection: (projectId: string, taskId: number) => void;
  markExamProjectStarted: (projectId: string) => void;
  resetExamProjectState: (projectId: string) => void;
  setCurrentProjectId: (projectId: string | null) => void;
}

const ExamContext = createContext<ExamContextType | null>(null);

export function useExamContext() {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error("useExamContext must be used within ExamProvider");
  }
  return context;
}

export function ExamProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>("practica");
  const [examActive, setExamActive] = useState(false);
  const [examFinished, setExamFinished] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(EXAM_DURATION);
  const [practicaId, setPracticaId] = useState<string | null>(null);
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [examResults, setExamResults] = useState<Record<
    string,
    TaskResult[]
  > | null>(null);
  const [examProjectSnapshots, setExamProjectSnapshots] = useState<
    Record<string, TaskResult[]>
  >({});
  const [examTaskSelections, setExamTaskSelections] = useState<
    Record<string, number[]>
  >({});
  const [examStartedProjects, setExamStartedProjects] = useState<string[]>([]);
  const [gradingAll, setGradingAll] = useState(false);
  const [examSyncFailed, setExamSyncFailed] = useState(false);
  const [retryingExamSave, setRetryingExamSave] = useState(false);
  const startedAtRef = useRef<Date | null>(null);
  const pendingExamRef = useRef<PendingExam | null>(null);

  const sendPendingExam = useCallback(async () => {
    const pending = pendingExamRef.current;
    if (!pending) return null;
    const record = await recordExamAttempt(
      pending.practicaId,
      pending.startedAt,
      pending.results,
      pending.clientId
    );
    if (record) pendingExamRef.current = null;
    return record;
  }, []);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const submittingRef = useRef(false);

  const clearExamRuntimeState = useCallback(() => {
    setCurrentProjectId(null);
    setExamProjectSnapshots({});
    setExamTaskSelections({});
    setExamStartedProjects([]);
  }, []);

  const submitExam = useCallback(async () => {
    if (submittingRef.current || !practicaId) return;
    submittingRef.current = true;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setGradingAll(true);

    try {
      const practica = findPractica(practicaId);
      if (!practica) {
        setExamResults({});
        setExamActive(false);
        setExamFinished(true);
        return;
      }

      const allResults: Record<string, TaskResult[]> = {};
      const resultMaps: Record<string, Record<string, boolean>> = {};

      for (const project of practica.projects) {
        let results: TaskResult[];

        if (examProjectSnapshots[project.id]) {
          results = examProjectSnapshots[project.id];
        } else {
          try {
            const gradeFn = await getGradeFunction(project.id);
            if (!gradeFn) throw new Error("Missing grading function");
            results = await gradeFn();
          } catch {
            results = project.tasks.map((task) => ({
              taskId: task.id,
              passed: false,
            }));
          }
        }

        allResults[project.id] = results;

        const resultMap: Record<string, boolean> = {};
        for (const result of results) {
          resultMap[String(result.taskId)] = result.passed;
        }
        resultMaps[project.id] = resultMap;
      }

      // The cloud record is the only official result. Keep what was sent so
      // a failed save can be retried; the client id makes retries idempotent.
      pendingExamRef.current = {
        practicaId: practica.id,
        startedAt: startedAtRef.current ?? new Date(),
        results: resultMaps,
        clientId: crypto.randomUUID(),
      };
      const record = await sendPendingExam();
      setExamSyncFailed(record === null);

      setExamResults(allResults);
      setExamActive(false);
      setExamFinished(true);
      setTimeRemaining(0);
      clearExamRuntimeState();
    } finally {
      setGradingAll(false);
      submittingRef.current = false;
    }
  }, [clearExamRuntimeState, examProjectSnapshots, practicaId, sendPendingExam]);

  const retryExamSave = useCallback(async () => {
    if (!pendingExamRef.current) return;
    setRetryingExamSave(true);
    try {
      const record = await sendPendingExam();
      setExamSyncFailed(record === null);
    } finally {
      setRetryingExamSave(false);
    }
  }, [sendPendingExam]);

  const startExam = useCallback((id: string) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setMode("examen");
    setPracticaId(id);
    startedAtRef.current = new Date();
    setExamSyncFailed(false);
    setExamActive(true);
    setExamFinished(false);
    setTimeRemaining(EXAM_DURATION);
    setCurrentProjectId(null);
    setExamResults(null);
    setExamProjectSnapshots({});
    setExamTaskSelections({});
    setExamStartedProjects([]);
    setGradingAll(false);
    submittingRef.current = false;
  }, []);

  const resetExam = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setMode("practica");
    setExamActive(false);
    setExamFinished(false);
    setTimeRemaining(EXAM_DURATION);
    setPracticaId(null);
    setCurrentProjectId(null);
    setExamResults(null);
    setExamProjectSnapshots({});
    setExamTaskSelections({});
    setExamStartedProjects([]);
    setGradingAll(false);
    submittingRef.current = false;
  }, []);

  const toggleExamTaskSelection = useCallback(
    (projectId: string, taskId: number) => {
      setExamTaskSelections((prev) => {
        const current = new Set(prev[projectId] ?? []);
        if (current.has(taskId)) current.delete(taskId);
        else current.add(taskId);

        return {
          ...prev,
          [projectId]: Array.from(current).sort((a, b) => a - b),
        };
      });
    },
    []
  );

  const saveExamProjectSnapshot = useCallback(
    (projectId: string, results: TaskResult[]) => {
      setExamProjectSnapshots((prev) => ({
        ...prev,
        [projectId]: results,
      }));
    },
    []
  );

  const markExamProjectStarted = useCallback((projectId: string) => {
    setExamStartedProjects((prev) =>
      prev.includes(projectId) ? prev : [...prev, projectId]
    );
  }, []);

  const resetExamProjectState = useCallback((projectId: string) => {
    setExamTaskSelections((prev) => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
    setExamProjectSnapshots((prev) => {
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
    setExamStartedProjects((prev) => prev.filter((id) => id !== projectId));
  }, []);

  useEffect(() => {
    if (!examActive || examFinished) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          void submitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [examActive, examFinished, submitExam]);

  return (
    <ExamContext.Provider
      value={{
        mode,
        setMode,
        examActive,
        examFinished,
        timeRemaining,
        practicaId,
        currentProjectId,
        examResults,
        examProjectSnapshots,
        examTaskSelections,
        examStartedProjects,
        gradingAll,
        examSyncFailed,
        retryingExamSave,
        retryExamSave,
        startExam,
        submitExam,
        resetExam,
        saveExamProjectSnapshot,
        toggleExamTaskSelection,
        markExamProjectStarted,
        resetExamProjectState,
        setCurrentProjectId,
      }}
    >
      {children}
    </ExamContext.Provider>
  );
}
